import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  Renderer2,
  TemplateRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { fromEvent, Observable } from 'rxjs';
import { debounceTime, map, pluck } from 'rxjs/operators';
import { IAnyObject } from '../../services/error/error.handler';
import { KeyPress, TypeaheadComponent } from '../typeahead/typeahead.component';
import { TypeaheadValue } from '../typeahead/typeahead.model';
import {
  CustomApiQuery,
  ExtendedTypeaheadOptions,
  TypeaheadRequestType
} from './extended-typeahead.model';
import { OirHttpService } from '../../services/http/oir-http.service';

@Component({
  selector: 'oir-extended-typeahead',
  templateUrl: './extended-typeahead.component.html',
  styleUrls: [
    './extended-typeahead.component.less',
    '../typeahead/typeahead.component.less'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ExtendedTypeaheadComponent),
      multi: true
    }
  ]
})
export class ExtendedTypeaheadComponent extends TypeaheadComponent {
  @Input() public template: TemplateRef<any>;
  @Input() public options: ExtendedTypeaheadOptions;
  @Input() private formatDataMethod: (...attr: [] | string[] | number[]) => any;
  @Input() private customApiQuery: CustomApiQuery;

  constructor(
    protected el: ElementRef,
    protected renderer: Renderer2,
    protected http: OirHttpService,
    protected cd: ChangeDetectorRef
  ) {
    super(el, renderer, http, cd);
  }

  public ngOnInit(): void {
    this.typeaheadModel = {
      active: false,
      animating: false,
      processing: false,
      spinnerActive: false
    };
    this.options = this.options || {
      pills: false,
      modelPropertyName: 'value',
      titlePropertyName: 'title',
      responsePropertyName: 'result',
      useREST: true,
      returnModelProperty: null,
      maxPills: 0,
      pillsOnEnter: false,
      pluckValueFromResult: null,
      minSearchAmount: 1,
      requestType: TypeaheadRequestType.GET
    };
    if (!this.options.maxPills) {
      this.options.maxPills = 0;
    }
    if (!this.options.minSearchAmount) {
      this.options.minSearchAmount = 1;
    }
    if (!this.options.requestType) {
      this.options.minSearchAmount = TypeaheadRequestType.GET;
    }

    if (this.options.pills && !Array.isArray(this.model)) {
      this.model = [];
    }

    if (this.input) {
      this.debounce = fromEvent(this.input.nativeElement, 'keyup').pipe(
        map((i: any) =>
          i.which === KeyPress.Tab || i.which === KeyPress.Enter
            ? null
            : i.currentTarget.value
        ),
        debounceTime(500)
      );
      this.debounce.subscribe(val => {
        if (val !== null) {
          this.onExtendedSearch(val);
        }
      });
    }
  }

  removePill($event: any, option: TypeaheadValue<any>): void {
    super.removePill($event, option);
    if (!this.disabled) {
      setTimeout(() => {
        this.modelChange.emit({
          selected: this.model,
          searchValue: this.query
        }); // changed line from original method
      }, 300);
    }
  }

  private onExtendedSearch(query: string): void {
    this.query = query;
    this.modelChange.emit({ selected: this.model, searchValue: this.query });
    if (query.length >= this.options.minSearchAmount) {
      if (query !== '') {
        this.typeaheadModel.spinnerActive = true;
        if (this.values.length > 0) {
          this.data = [];
          const results = this.values.filter(
            x => (
              x[this.options.titlePropertyName] &&
              x[this.options.titlePropertyName].toLowerCase().indexOf(query.toLowerCase()) >= 0
            )
          );
          if (results && results.length > 0) {
            this.data = results.slice(0, 10);
            this.toggleDropdown();
            this.typeaheadModel.active = true;
            this.clearActive();
            this.registerListener();
          } else {
            this.onNoResultsFound.emit(query);
          }
        } else {
          if (this.apiPath) {
            this.unsubscribeOnSearch();
            let searchResult = this.fetchDropdownValues();

            if (this.options.pluckValueFromResult) {
              searchResult = searchResult.pipe(
                pluck(this.options.pluckValueFromResult)
              );
            }
            this.onSearchSubscription = searchResult.subscribe(
              (response: any) => {
                this.data = [];
                if (response && response.length) {
                  if (this.formatDataMethod) {
                    this.data = this.formatDataMethod(response.slice(0, 10));
                  } else {
                    this.data = this.defaultDataFormat(response.slice(0, 10));
                  }
                }
                this.toggleDropdown();
                this.typeaheadModel.active = true;
                this.clearActive();
                this.registerListener();
              }
            );
          } else {
            this.data = [];
            this.toggleDropdown();
            this.cd.detectChanges();
            this.typeaheadModel.active = true;
            this.clearActive();
            this.registerListener();
          }
        }
      } else {
        this.query = '';
        if (!this.options.pills) {
          this.model = {};

          this.propagateChange(this.model);
          this.modelChange.emit({
            selected: this.model,
            searchValue: this.query
          });
        }

        this.close();
      }
    }
  }

  public selectExtended(option: TypeaheadValue<any>): void {
    if (!option.disabled && !option.selected) {
      const value = this.getCurrentValue(option);
      if (!this.options.pills) {
        this.query = option.title;
        this.model = value;
      } else if (this.options.pills) {
        if (!(this.model instanceof Array) || this.model.length === undefined) {
          this.model = [];
        }
        this.query = '';
        // check to not add duplicate title to pills
        if (
          this.model.filter(
            x =>
              JSON.stringify(
                x[this.options.titlePropertyName]
                  .toString()
                  .toLowerCase()
                  .trim()
              ) ===
              JSON.stringify(
                value[this.options.titlePropertyName]
                  .toString()
                  .toLowerCase()
                  .trim()
              )
          ).length === 0
        ) {
          this.model.push(value);
        }
        this.inputDisabled =
          Array.isArray(this.model) &&
          this.options.maxPills !== 0 &&
          this.model.length >= this.options.maxPills;
      }
      if (!this.options.pills && this.options.returnModelProperty) {
        this.propagateChange(this.model[this.options.returnModelProperty]);
      } else {
        this.propagateChange(this.model);
      }
      this.modelChange.emit({ selected: this.model, searchValue: this.query });
      this.input.nativeElement.focus();
      this.close();
      this.data = [];
    }
  }

  private defaultDataFormat(values: any[]): TypeaheadValue<any>[] {
    if (typeof values[0] === 'string') {
      values = values.map((v, i) => {
        return {
          title: v,
          value: i
        };
      });
    } else {
      values = values.map((v, i) => {
        return {
          title: v[this.options.titlePropertyName] || v.title,
          value: v.value ? v.value : i
        };
      });
    }
    return values;
  }

  private fetchDropdownValues(): Observable<any> {
    const requestData = this.customApiQuery
      ? {
          ...this.customApiQuery.requestBody,
          [this.customApiQuery.searchValueProperty]: this.query
        }
      : this.options.requestType === TypeaheadRequestType.POST
      ? {}
      : { query: this.query };
    if (this.options.requestType === TypeaheadRequestType.POST) {
      return this.http.post<any>({
        path: this.apiPath,
        body: requestData,
        hideSpinner: true,
        responseType: 'json'
      });
    } else {
      return this.http.get<any>({
        path: this.apiPath,
        query: requestData,
        hideSpinner: true
      });
    }
  }
}
